/**
 * PUDO Receive - Intake package from driver
 * Scans Package QR to receive into PUDO inventory
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  QrCode,
  Camera,
  CheckCircle,
  AlertCircle,
  Package,
  Inbox,
  Scan
} from 'lucide-react';

const PudoReceive = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScanning(true);
      toast.info('Camera ready - scan Package QR code');
    } catch (err) {
      setCameraError('Camera access denied. Please use manual entry.');
      toast.error('Could not access camera');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const handleReceive = async (qrData) => {
    if (processing) return;
    
    setProcessing(true);
    stopCamera();
    
    try {
      // Use the PUDO receive endpoint
      const response = await api.post('/pudo/receive', {
        qr_data: qrData,
        scan_type: 'receive',
        location: null // Could add GPS here
      });
      
      setResult({
        success: true,
        message: response.data.message || 'Package received successfully!',
        data: response.data
      });
      
      toast.success('Package received into inventory!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to receive package';
      setResult({
        success: false,
        message: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      // For manual entry, construct QR data format
      const qrData = JSON.stringify({ package_id: manualCode.trim() });
      handleReceive(qrData);
    }
  };

  const resetScan = () => {
    setResult(null);
    setManualCode('');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pudo-receive-page">
      {/* Header */}
      <header className="bg-purple-600 text-white px-4 py-4">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/pudo')}
            className="p-2 hover:bg-purple-500 rounded-full text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Inbox className="h-6 w-6" />
            <h1 className="text-lg font-bold">Receive Package</h1>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-6">
        {/* Instructions */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <h2 className="font-semibold text-purple-800 mb-2">Intake from Driver</h2>
          <p className="text-sm text-purple-700">
            Scan the <strong>Package QR code</strong> when a driver delivers a package to your PUDO point.
            This will add the package to your inventory.
          </p>
        </div>

        {/* Result Display */}
        {result && (
          <div 
            className={`rounded-xl p-6 text-center ${
              result.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}
            data-testid="scan-result"
          >
            {result.success ? (
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            )}
            <h3 className={`text-xl font-bold mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? 'Package Received!' : 'Receive Failed'}
            </h3>
            <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.message}
            </p>
            {result.success && result.data?.leg && (
              <div className="mt-4 text-left bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Package ID:</p>
                <p className="font-mono text-sm">{result.data.leg.package_id?.slice(0, 12)}...</p>
              </div>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              <Button
                onClick={resetScan}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="scan-another-btn"
              >
                Scan Another
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pudo')}
                data-testid="back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Scanner UI */}
        {!result && (
          <>
            {/* Camera View */}
            {scanning ? (
              <div className="relative rounded-xl overflow-hidden bg-black aspect-square">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-white rounded-2xl opacity-70" />
                </div>
                <Button
                  onClick={stopCamera}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500 hover:bg-red-600"
                >
                  Stop Camera
                </Button>
                <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                  Position Package QR in frame
                </p>
              </div>
            ) : (
              <Button
                onClick={startCamera}
                disabled={processing}
                className="w-full py-8 bg-purple-600 hover:bg-purple-700 text-white rounded-xl flex flex-col items-center gap-2"
                data-testid="start-camera-btn"
              >
                <Camera className="h-10 w-10" />
                <span className="font-semibold">Start Camera Scan</span>
              </Button>
            )}

            {/* Camera Error */}
            {cameraError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-sm text-amber-700">
                {cameraError}
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300" />
              <span className="text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>

            {/* Manual Entry */}
            <form onSubmit={handleManualSubmit} className="space-y-4" data-testid="manual-entry-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Package ID / Tracking Code
                </label>
                <Input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="e.g., PKG-ABC123 or scan data"
                  className="w-full"
                  data-testid="manual-input"
                />
              </div>
              <Button
                type="submit"
                disabled={!manualCode.trim() || processing}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white"
                data-testid="manual-submit-btn"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Scan className="h-4 w-4" />
                    Receive Package
                  </span>
                )}
              </Button>
            </form>
          </>
        )}
      </main>
    </div>
  );
};

export default PudoReceive;
